/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 메인페이지의 학교 상세 정보
 * 버전 : 1.0
 * 파일명 : SchoolListDetail.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================
 * 수정 : 2025-12-26, 강동형 : 학교 번호, 웹사이트 주소추가
 * 수정 : 2025-12-29, 강동형 : 기본정보 탭 스크롤 안되는 문제 수정
 * 수정 : 2025-12-30, 강동형 : 학과정보탭 UI 추가
 * 수정 : 2026-01-09, 강동형 : Tmap 연동기능 추가
 * 수정 : 2026-02-02, 강동형 : 홈페이지 링크 기능 추가
 * 수정 : 2026-02-04, 강동형 : 링크 오류 수정
 *================================================================*/

import React, { useState } from 'react'
import { Typography, Switch, Box, IconButton } from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import { BottomSheetType, ExclusionType } from 'src/types/SchoolType';
import SchoolListDetailTab00 from './SchoolListDetailTab00';
import SchoolListDetailTab01 from './SchoolListDetailTab01';
import { getDayOfWeek } from 'src/utils/DateUtils';
import { patchExclustion } from 'src/api/SchoolService';

// 이미지 import
import WatchIcon from '@mui/icons-material/WatchLater';
import { Icon_AiMotion } from '../common/CommonIcons';
import { AppImage } from 'src/assets/images/AppImage';
import styled from "styled-components";
import { openTmap, toAbsoluteUrl } from 'src/utils/Etc';
import SchoolListDetailTab02 from '../schoolSearch/SchoolListDetailTab02';

// HorizontalLayout 컴포넌트 정의
const HorizontalLayout = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const CategoryHead = styled.span`
    opacity: 0.5;
`;

const TagContainer = styled.div`
    display: flex;
    gap: 10px;

    position: absolute;
    top: calc(100% + 6px);
    font-size: 12px;
    font-weight: 500;
`;

const ThicDivider = styled.div`
    margin-bottom: 16px;
    margin-left: -24px;
    margin-right: -24px;
    min-height: 10px;
    background-color: #f5f5f5;
`;

const ParseTypeWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    background-color: white;
    color: rgba(30, 148, 104, 1);
    border: 0.87px solid rgba(30, 148, 104, 1);
    padding: 3px 7px;
    border-radius: 4px;
    white-space: nowrap;
`;

const TagWarpper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    background-color: #f4f4f4;
    color: #035234;
    padding: 3px 7px;
    border-radius: 4px;
    white-space: nowrap;
`;

const TabWrapper = styled.div`
    position: sticky;
    top: -24px;
    background-color: white;
    z-index: 1;
`;

const TabList = styled.div`
    display: flex;
    gap: 20px;
    width: fit-content;
`;

const TabButton = styled.div<{ $selected: boolean }>`
    font-weight: bold;
    color: ${({ $selected }) => ($selected ? "black" : "#7F7F89")};
    border-bottom: 2px solid ${({ $selected }) => ($selected ? "black" : "transparent")};
    padding: 15px 0;
    font-size: 16px;
    cursor: pointer;
`;

const TabDivider = styled.div`
    height: 1px;
    background-color: #e0e0e0;
    margin: 0 -24px;
`;

const DetailWrapper = styled.div`
    display:flex;
    flex-direction:column;
    height: calc(100vh - 510px);

    min-height: 0;   /* ✅ flex 내부 스크롤 필수 */
    overflow-y: auto;
`;

const PositionSpan = styled.span`
    display: inline-block;
    max-width: 100%;
    overflow-x: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-decoration:underline;
    cursor: pointer;
`

interface Props{
    school : BottomSheetType
    setAiState : React.Dispatch<React.SetStateAction<boolean>>
}

const SchoolListDetail = ({school, setAiState}:Props) => {
    const [tab,setTab] =useState(0)
    const [checked, setChecked] = useState(!school.excluded)

    let parseType = ''
        if (school.school_type === '특성화고등학교') {
        parseType = '특성화고'
        } else if (school.school_type === '특수목적고등학교') {
        parseType = '특목고'
        } else if (school.school_type === '자율고등학교') {
        parseType = '자율고'
        }
    
    const handleExclude = async ({school_class,school_id,excluded}:ExclusionType)=>{
        await patchExclustion({school_class,school_id,excluded})
        setChecked((state)=>!state)
    }

  return (
    <div style={{padding:'0px 20px'}}>
    {/* 학교 이름 */}
    <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom='24px'>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', marginBottom: '4px', fontSize:'26px', position:'relative'}}>
        {school.school_name}
            <TagContainer>
                {parseType &&
                <ParseTypeWrapper>
                    {parseType}
                </ParseTypeWrapper>}
                {school.military_agreement &&
                <TagWarpper>
                    군협약
                </TagWarpper>
                }
                {school.rotc &&
                <TagWarpper>
                    학군단
                </TagWarpper>
                }
                {school.special_major_list.length > 0 &&
                <TagWarpper>
                    우대학과
                </TagWarpper>
                }
            </TagContainer>
        </Typography>
        <IconButton onClick={()=>setAiState(true)}>
            <Icon_AiMotion/>
        </IconButton>
    </Box>
    {/* 주소 */}
    <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px' }}>
        <LocationOnIcon fontSize="small" sx={{ color: '#DDDDDD', marginRight: 1 }} />
        <PositionSpan style={{}} onClick={()=>openTmap({latitude:school.location_latitude, longitude:school.location_longitude})}>
            {school.address ||'상세 주소 없음'}
        </PositionSpan>
    </Typography>
    {/* 학생 수 */}
    <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px' }}>
        <PersonIcon fontSize="small" sx={{ color: '#DDDDDD', marginRight: 1 }} />
        <CategoryHead>학생 수 &nbsp;</CategoryHead>
        {school.total_student_cnt  ? `${school.total_student_cnt}명` : '정보 없음'}
    </Typography>
    {/* 최근 방문 날짜 */}
    <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px' }}>
        <WatchIcon fontSize="small" sx={{ color: '#DDDDDD', marginRight: 1 }} />
        {school.visited_at ? (
            <>
                <CategoryHead>최근 방문 날짜 &nbsp;</CategoryHead>{school.visited_at}{getDayOfWeek(school.visited_at)}
            </>
            ) : (
            <> <CategoryHead>최근 방문 날짜 &nbsp;</CategoryHead>방문 기록 없음</>
        )}
    </Typography>
    {/* 전화번호 */}
    <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px', gap:'8px' }}>
        <AppImage name='IconPhone'/>
        {school.tel}
    </Typography>
    {/* 홈페이지 */}
    <Typography variant="body2" display="flex" alignItems="center" sx={{ marginBottom: '8px', gap:'8px', width:'100%' }}>
        <AppImage name='IconGlobal'/>
        <a href={toAbsoluteUrl(school.homepage)} target='_blank' rel='noopener noreferrer' title={school.homepage}
        style={{whiteSpace:'nowrap', textOverflow:'ellipsis',flex:1, minWidth:0, overflow:'hidden', textDecoration:'underline', color:'#808089'}}>
            {school.homepage}
        </a>
    </Typography>
    <ThicDivider/>
    {/* 홍보 대상 포함 */}
    <HorizontalLayout>
        <Box>
        <Typography variant="body1">홍보 대상 포함</Typography>
        <Typography variant="body2" sx={{ marginTop: '4px' }}>홍보 대상에서 제외하고 싶으시면 비활성화해주세요</Typography>
        </Box>
        <Switch
        checked={checked}
        onChange={() => handleExclude({
            school_class:school.school_class,
            school_id: school.school_id,
            excluded: checked
        })}
        sx={{
            //스위치 버튼 Container 크기
            width: '37px',
            height: '22px',
            padding: 0,
            '& .MuiSwitch-switchBase': {
                //내부 패딩, 마진 값
                padding: '1px',
                margin: 0,
                '&.Mui-checked': {
                        //애니메이션 세부사항 (X축으로 얼마나 이동할건지)
                        transform: 'translateX(15px)',
                        color: 'white',
                        '& + .MuiSwitch-track': {
                            //On일때 배경색
                        backgroundColor: '#06799B',
                        opacity: 1,
                        border: 0,
                        },
                        '& .MuiSwitch-thumb': {
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.3)',
                        }
                    },
                },
            '& .MuiSwitch-thumb': {
                //스위치 크기 및 스타일
                width: '20px',
                height: '20px',
                border: 'none'
            },
            '& .MuiSwitch-track': {
            //Off일때 색
            borderRadius: 26 / 2,
            backgroundColor: '#ccc',
            opacity: 1,
            },
        }}
        />
    </HorizontalLayout>
    <ThicDivider style={{marginBottom:'0px'}}/>
    {/* 탭 */}
    <TabWrapper>
    <TabList>
        <TabButton $selected={tab === 0} onClick={() => setTab(0)}>
        기본 정보
        </TabButton>
        <TabButton $selected={tab === 1} onClick={() => setTab(1)}>
        홍보 기록
        </TabButton>
        <TabButton $selected={tab === 2} onClick={() => setTab(2)}>
        학과정보
        </TabButton>
    </TabList>
    <TabDivider />
    </TabWrapper>
    <DetailWrapper>
        {tab === 0 && (
            <SchoolListDetailTab00 school={school}/>
        )}
        {tab === 1 && (
            <SchoolListDetailTab01 school={school}/>
        )}
        {tab === 2 && (
            <SchoolListDetailTab02 majorList={school.all_major_list}/>
        )}
    </DetailWrapper>
    </div>
    )
}

export default SchoolListDetail